import { useCallback, useRef } from 'react';
import { randomUUID } from 'utils/uuid';
import {
  trackVideoFeedItemView,
  trackClick,
} from 'services/snowplowAnalytics/snowplowAnalytics';
import { useSnowplow } from 'contexts/SnowplowContext';
import { useCookieConsent } from 'contexts/CookieConsentContext/CookieConsentContext';
import type { OnItemVisible, OnItemClick } from '@yleisradio/lyhyet-web-items';
import { Consent } from 'types/yleConsentSdk';

const ifConsentAllowsRecommendationData = <T>(
  consent: Consent | null,
  data: T
): T | undefined => {
  if (consent?.development || consent?.personalization) {
    return data;
  }
  return undefined;
};

export const useVideoFeedTracking = (
  contentIndex: number,
  title: string | undefined
) => {
  const { isSnowplowReady } = useSnowplow();
  const consent = useCookieConsent();
  const viewedItems = useRef<Set<string>>(new Set());
  const clickedItems = useRef<Set<string>>(new Set());
  const viewUuids = useRef<Map<string, string>>(new Map());

  const handleItemVisible: OnItemVisible = useCallback(
    ({ item, index }) => {
      if (!isSnowplowReady || viewedItems.current.has(item.id)) return;

      // All other projects first check that source type is "article" before
      // tracking, so analytics is sent with correct snowplow entity.
      //
      // Technically, for this project it is perhaps not strictly necessary
      // since we don't send entity of type "article" but a generic "entity"
      // instead, but... there is no guarantee that item.id will be something
      // that can be given to snowplow in field of type "entity".
      //
      // So, let's be consistent with other projects. All of this probably
      // will be gone "soon" when video shorts flow here through areena-api.
      const source = item.source;
      if (source.type !== 'article') {
        return;
      }

      viewedItems.current.add(item.id);
      const viewUuid = randomUUID();
      viewUuids.current.set(item.id, viewUuid);

      trackVideoFeedItemView({
        entityId: source.articleId,
        viewUuid,
        itemIndex: index,
        contentIndex,
        label: title,
        recommendationId: ifConsentAllowsRecommendationData(
          consent,
          item.meta?.recommendation?.uuid
        ),
        recommendationMethod: ifConsentAllowsRecommendationData(
          consent,
          item.meta?.recommendation?.method
        ),
        tests:
          ifConsentAllowsRecommendationData(consent, item.meta?.tests) ?? [],
      });
    },
    [isSnowplowReady, consent, contentIndex, title]
  );

  const handleItemClick: OnItemClick = useCallback(
    ({ item }) => {
      const viewUuid = viewUuids.current.get(item.id);
      if (!isSnowplowReady || !viewUuid || clickedItems.current.has(item.id))
        return;

      clickedItems.current.add(item.id);
      trackClick({ action: 'link', viewUuid });
    },
    [isSnowplowReady]
  );

  return { handleItemVisible, handleItemClick };
};
